import { StateCreator } from "zustand";

/**
 * Chat slice state.
 *
 * NOTE: This slice only holds client-only UI state (sheet open/closed).
 * TalkJS data comes from the server via React Query or Context.
 */
export type ChatSliceState = {
  /** Whether the chat sheet is open */
  isChatOpen: boolean;
};

/**
 * Chat slice actions.
 */
export type ChatSliceActions = {
  openChat: () => void;
  closeChat: () => void;
  toggleChat: () => void;
};

/**
 * Full chat slice type.
 */
export type ChatSlice = ChatSliceState & ChatSliceActions;

/**
 * Initial state for chat slice.
 */
export const initialChatState: ChatSliceState = {
  isChatOpen: false,
};

/**
 * Creates the chat slice for Zustand store.
 *
 * @example
 * ```typescript
 * const useStore = create<ChatSlice>()((...a) => ({
 *   ...createChatSlice(...a),
 * }));
 *
 * // In a component:
 * const isChatOpen = useStore((s) => s.isChatOpen);
 * const openChat = useStore((s) => s.openChat);
 * ```
 */
export const createChatSlice: StateCreator<ChatSlice, [], [], ChatSlice> = (
  set
) => ({
  ...initialChatState,

  openChat: () => set({ isChatOpen: true }),

  closeChat: () => set({ isChatOpen: false }),

  toggleChat: () => set((state) => ({ isChatOpen: !state.isChatOpen })),
});

// --- Selectors ---
export const chat = {
  sheet: (s: ChatSlice) => ({
    isOpen: s.isChatOpen,
    open: s.openChat,
    close: s.closeChat,
    toggle: s.toggleChat,
  }),
};

